import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2, X, Loader2, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BulkActionBarProps {
  selectedCount: number;
  totalCount: number;
  allSelected: boolean;
  onSelectAll: (checked: boolean) => void;
  /** Omit to hide the bulk-delete button (e.g. for roles without delete). */
  onDelete?: () => void;
  onClear: () => void;
  isDeleting?: boolean;
  onDuplicate?: () => void;
  isDuplicating?: boolean;
  /**
   * Ação extra opcional (hoje: "definir custo" no catálogo). Recebe o ícone
   * pronto para o mobile mostrar só ele — a barra é uma pílula fixa e o rótulo
   * já é `hidden sm:inline` nos outros botões pelo mesmo motivo (armadilha #28).
   */
  extraAction?: {
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
  };
  className?: string;
}

export function BulkActionBar({
  selectedCount,
  totalCount,
  allSelected,
  onSelectAll,
  onDelete,
  onClear,
  isDeleting = false,
  onDuplicate,
  isDuplicating = false,
  extraAction,
  className,
}: BulkActionBarProps) {
  if (selectedCount === 0) return null;

  return (
    <div
      className={cn(
        'fixed bottom-36 left-1/2 z-50 -translate-x-1/2 transform',
        'flex items-center gap-2 sm:gap-3 rounded-full border bg-background px-3 sm:px-4 py-2 shadow-lg',
        'animate-in fade-in slide-in-from-bottom-4 duration-200',
        className
      )}
    >
      <div className="flex items-center gap-2">
        <Checkbox
          checked={allSelected}
          onCheckedChange={onSelectAll}
          aria-label="Selecionar todos"
        />
        {/* Com uma TERCEIRA ação (o "Custo" do catálogo) a pílula media 366px
            num viewport de 360 e vazava dos dois lados — medido, não estimado
            (armadilha #28). Abaixo de 380px a palavra some e fica só o número;
            o rótulo completo continua igual nas telas que não têm ação extra,
            para não regredir a barra que já existia. */}
        <span className="text-sm font-medium whitespace-nowrap">
          {selectedCount}
          <span className={cn(extraAction && 'hidden min-[380px]:inline')}>
            {' '}
            {selectedCount === 1 ? 'selecionado' : 'selecionados'}
          </span>
        </span>
      </div>

      <div className="h-4 w-px bg-border" />

      {onDuplicate && (
        <Button3D
          color="green"
          size="sm"
          onClick={onDuplicate}
          disabled={isDuplicating || isDeleting}
          className="gap-1.5"
        >
          {isDuplicating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">Duplicar</span>
        </Button3D>
      )}

      {extraAction && (
        <Button3D
          color="green"
          size="sm"
          onClick={extraAction.onClick}
          disabled={isDuplicating || isDeleting}
          className="gap-1.5"
          aria-label={extraAction.label}
        >
          {extraAction.icon}
          <span className="hidden sm:inline">{extraAction.label}</span>
        </Button3D>
      )}

      {onDelete && (
        <Button3D
          color="red"
          size="sm"
          onClick={onDelete}
          disabled={isDeleting || isDuplicating}
          className="gap-1.5"
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">Excluir</span>
        </Button3D>
      )}

      <Button
        variant="ghost"
        size="icon"
        onClick={onClear}
        className="h-8 w-8"
        aria-label="Limpar seleção"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
